import { z } from "zod";

/**
 * Shared DTO cho Admin Observability board + Alert rules (US-AE9.F1-0029 · @UC9080 metrics-health /
 * @UC9081 alert-rules). Một nguồn sự thật cho api (S01/S03) và admin web (S06). Portal CHỈ ĐỌC
 * Prometheus/health (AF9.1 Q&A) — DTO chỉ mô tả snapshot đọc + cấu hình rule; KHÔNG field thu metric.
 *
 * BẤT BIẾN (invariants):
 * - B1: số lớn / minor units serialize STRING trên wire (không float/number JS). reconcileDriftMinor
 *   = string (BigInt-safe). thresholdValue rule = string (int minor units hoặc ratio) — no float.
 * - `.strict()` cho body ghi (AlertRulePatch) — chống over-post/field lạ tại biên.
 * - C1 no-SMS: channel ∈ {teams, email} — KHÔNG sms.
 * - G4: DTO KHÔNG mang webhook URL/secret (đích alert lấy từ env phía worker).
 */

// ============================================================
// @UC9080 — Observability snapshot (metrics + health) — READ
// ============================================================

/** reconcileDriftMinor = BigInt minor units as string (no float). "0" khi không lệch. */
const driftMinorString = z
  .string()
  .regex(/^-?\d+$/, "reconcileDriftMinor phải là số nguyên minor units (không float)");

/** Trạng thái 1 dependency health: 'up' nhận traffic; 'down' fail-closed (503). */
export const healthCheckStatusSchema = z.enum(["up", "down"]);
export type HealthCheckStatus = z.infer<typeof healthCheckStatusSchema>;

/**
 * Health rollup cho portal (đọc /v1/health/ready). live = process OK; ready = nhận traffic được.
 * checks phản ánh db/redis/migration (S03 bổ sung migration). KHÔNG lộ chi tiết lỗi (chỉ up/down).
 */
export const observabilityHealthSchema = z
  .object({
    live: z.boolean(),
    ready: z.boolean(),
    checks: z
      .object({
        db: healthCheckStatusSchema,
        redis: healthCheckStatusSchema,
        migration: healthCheckStatusSchema,
      })
      .strict(),
  })
  .strict();

export type ObservabilityHealth = z.infer<typeof observabilityHealthSchema>;

/**
 * Snapshot metrics + health cho board (@UC9080 AC1+AC2). Đọc registry Prometheus (reuse
 * MetricsService) — KHÔNG tự thu. Latency ms (number OK — không phải tiền). reconcileDriftMinor =
 * string (BigInt-safe). Tỉ lệ = number [0..1] (5xx ratio, job fail ratio). RPS/outboxLag = number.
 */
export const observabilitySnapshotSchema = z
  .object({
    /** Tỉ lệ request 5xx trên tổng request (0..1). */
    errorRate: z.number().min(0),
    /** Độ trễ p50/p95/p99 (mili-giây). */
    latencyP50Ms: z.number().min(0),
    latencyP95Ms: z.number().min(0),
    latencyP99Ms: z.number().min(0),
    /** Requests-per-second (trung bình cửa sổ). */
    rps: z.number().min(0),
    /**
     * Tuổi (giây) outbox event pending lâu nhất. `null` khi KHÔNG có nguồn thật
     * (US-AE7.F6-0160 S01/S11 @UC9168/@UC9174: trước đây api đọc gauge chưa từng `.set()`
     * ⇒ luôn `0` tone success — SPLIT-BRAIN. Không nguồn ⇒ `null` để UI hiện `—`, không bịa `0`).
     */
    outboxLagSeconds: z.number().min(0).nullable(),
    /** Σ|reconcile drift| minor units as string (no float). "0" khi khớp. */
    reconcileDriftMinor: driftMinorString,
    /** Tỉ lệ job nền thất bại (0..1). `null` khi không có nguồn thật (S11 — không bịa `0`). */
    jobFailRate: z.number().min(0).nullable(),
    /** Thời điểm chụp snapshot (ISO). */
    capturedAt: z.string(),
    health: observabilityHealthSchema,
  })
  .strict();

export type ObservabilitySnapshot = z.infer<typeof observabilitySnapshotSchema>;

// ============================================================
// @UC9081 — Alert rules — READ list + PATCH threshold
// ============================================================

/** Mức nghiêm trọng rule (khớp mock resources.ts sev). */
export const alertSeveritySchema = z.enum(["high", "medium"]);
export type AlertSeverity = z.infer<typeof alertSeveritySchema>;

/** Kênh phát alert — Teams/email (NO SMS, guardrail C1). Khớp mock channel. */
export const alertChannelSchema = z.enum(["teams", "email"]);
export type AlertChannel = z.infer<typeof alertChannelSchema>;

/** Trạng thái rule (khớp mock status). paused → worker BỎ QUA eval. */
export const alertRuleStatusSchema = z.enum(["enabled", "paused"]);
export type AlertRuleStatus = z.infer<typeof alertRuleStatusSchema>;

/** Toán tử so ngưỡng khi eval. */
export const alertComparatorSchema = z.enum(["gt", "gte", "lt"]);
export type AlertComparator = z.infer<typeof alertComparatorSchema>;

/** thresholdValue = string (BigInt-safe/no-float): int minor units cho drift, hoặc ratio "0.01". */
const thresholdValueString = z
  .string()
  .min(1)
  .regex(/^\d+(\.\d+)?$/, "thresholdValue phải là số không âm dạng chuỗi (int hoặc ratio)");

export const ALERT_RUNBOOK_URL_MAX_LENGTH = 500;
export const ALERT_RULE_NAME_MAX_LENGTH = 200;
export const ALERT_RULES_MAX_LIMIT = 100;

/**
 * Một AlertRule (read). Mirror model AlertRule (S05) + mock resources.ts (name/cond/sev/channel/
 * status). thresholdValue = string. runbookUrl có thể null. KHÔNG mang webhook/secret (G4).
 */
export const alertRuleSchema = z
  .object({
    id: z.string(),
    ruleKey: z.string(),
    name: z.string(),
    condition: z.string(),
    metric: z.string(),
    comparator: alertComparatorSchema,
    thresholdValue: thresholdValueString,
    windowMinutes: z.number().int().min(0).nullable(),
    severity: alertSeveritySchema,
    channel: alertChannelSchema,
    runbookUrl: z.string().nullable(),
    status: alertRuleStatusSchema,
    updatedAt: z.string(),
  })
  .strict();

export type AlertRule = z.infer<typeof alertRuleSchema>;

/** Envelope list `{ data, pageInfo }` (cursor pagination). */
export const alertRuleListResponseSchema = z
  .object({
    data: z.array(alertRuleSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type AlertRuleListResponse = z.infer<typeof alertRuleListResponseSchema>;

/**
 * Body PATCH /admin/alerts/rules/:id (@UC9081 AC3) — subset ghi được: threshold/comparator/window/
 * severity/channel/status/runbookUrl. `.strict()` chặn field lạ (over-post/IDOR). Mọi field optional
 * (partial update) — nhưng phải có ≥1 field (refine). KHÔNG cho sửa ruleKey/metric (định danh eval).
 */
export const alertRulePatchSchema = z
  .object({
    thresholdValue: thresholdValueString.optional(),
    comparator: alertComparatorSchema.optional(),
    windowMinutes: z.number().int().min(0).nullable().optional(),
    severity: alertSeveritySchema.optional(),
    channel: alertChannelSchema.optional(),
    status: alertRuleStatusSchema.optional(),
    runbookUrl: z.string().max(ALERT_RUNBOOK_URL_MAX_LENGTH).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: "PATCH cần ít nhất một field để cập nhật",
  });

export type AlertRulePatch = z.infer<typeof alertRulePatchSchema>;

/** Query GET /admin/alerts/rules — cursor + limit + filter status. */
export const alertRuleListQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ALERT_RULES_MAX_LIMIT).optional(),
    status: alertRuleStatusSchema.optional(),
  })
  .strict();

export type AlertRuleListQuery = z.infer<typeof alertRuleListQuerySchema>;

// ============================================================
// @UC9160 — Alert-rule CREATE (structured builder) + DELETE
// ============================================================

/**
 * Registry metric ĐÓNG cho builder có cấu trúc (US-AE-IMP2-T13 · @UC9160). Người tạo rule CHỌN metricKey
 * từ danh sách này (dropdown) — KHÔNG gõ biểu thức tự do / metric thô (tránh injection/eval + eval-safe).
 * Mỗi entry map ra `metric` (định danh worker eval), `ruleKey` (định danh dedup) và `condition`
 * (chuỗi người-đọc) do SERVER dẫn xuất — client KHÔNG gửi các field này. `unit` để FE hiển thị đơn vị.
 *
 * `observed` (S07 — không tạo niềm tin sai): true khi worker THẬT SỰ quan sát được metric này (job
 * alert-eval `createDbMetricSource.valueFor` trả giá trị) → rule sẽ được đánh giá & có thể fire. false
 * khi worker CHƯA quan sát (valueFor trả null → job skip → rule KHÔNG BAO GIỜ fire). Đây là NGUỒN SỰ
 * THẬT DUY NHẤT để UI cảnh báo (disable trong builder + badge "chưa được đánh giá" ở list). Phải khớp
 * đúng những gì `apps/worker/src/jobs/alert-eval.job.ts` thật sự eval — khi worker mở rộng valueFor,
 * cập nhật `observed` tại đây (test S07 chốt đồng bộ). Metric giữ trong registry để builder không khoá
 * cứng (worker mở rộng sau), nhưng luôn nằm trong enum đóng.
 */
export const ALERT_METRIC_REGISTRY = {
  reconcile_drift_minor: {
    metric: "goppy_reconcile_drift_minor",
    ruleKey: "reconcile_drift",
    condition: "reconcile_drift_minor",
    unit: "minor", // minor units (int, no float)
    labelVi: "Lệch đối soát (minor)",
    labelEn: "Reconcile drift (minor)",
    observed: true, // worker valueFor → dbDriftMinor
  },
  outbox_lag_seconds: {
    metric: "goppy_outbox_lag_seconds",
    ruleKey: "outbox_lag",
    condition: "outbox_lag_seconds",
    unit: "seconds",
    labelVi: "Độ trễ outbox (giây)",
    labelEn: "Outbox lag (seconds)",
    observed: true, // worker valueFor → dbOutboxLagSeconds
  },
  http_5xx_ratio: {
    metric: "goppy_http_5xx_ratio",
    ruleKey: "http_5xx",
    condition: "http_5xx_ratio",
    unit: "ratio", // 0..1
    labelVi: "Tỉ lệ lỗi 5xx",
    labelEn: "5xx error ratio",
    observed: false, // worker valueFor → null (chỉ có ở prom-client API) → không bao giờ fire
  },
  job_fail_ratio: {
    metric: "goppy_job_fail_ratio",
    ruleKey: "job_fail",
    condition: "job_fail_ratio",
    unit: "ratio",
    labelVi: "Tỉ lệ tác vụ lỗi",
    labelEn: "Job fail ratio",
    observed: false, // worker valueFor → null → không bao giờ fire
  },
  // 🔴 AI2D-T15 (@UC0449): lượt làn ĐỌC của trợ lý lùi vì nhà cung cấp / lỗi / hết ngân sách ÷ (lượt lùi + lời gọi đã trả về) trong
  // `windowMinutes`; dưới 5 lượt lùi ⇒ 0. Gợi ý: comparator gte · ngưỡng 0.5 · cửa sổ 15′.
  ai_read_degraded_ratio: {
    metric: "goppy_ai_read_degraded_ratio",
    ruleKey: "ai_read_degraded",
    condition: "ai_read_degraded_ratio",
    unit: "ratio",
    labelVi: "Tỉ lệ lượt trợ lý đọc chạy chế độ kém",
    labelEn: "AI read lane degraded ratio",
    observed: true, // worker valueFor → dbReadDegraded (ai_audit_log)
  },
} as const satisfies Record<
  string,
  {
    metric: string;
    ruleKey: string;
    condition: string;
    unit: string;
    labelVi: string;
    labelEn: string;
    observed: boolean;
  }
>;

export type AlertMetricKey = keyof typeof ALERT_METRIC_REGISTRY;

/** Enum đóng metricKey builder — reject metric thô/biểu thức tự do (guardrail eval-safe). */
export const alertMetricKeySchema = z.enum(
  Object.keys(ALERT_METRIC_REGISTRY) as [AlertMetricKey, ...AlertMetricKey[]],
);

/** Đơn vị hiển thị metric (FE gợi ý ô ngưỡng). */
export const alertMetricUnitSchema = z.enum(["minor", "seconds", "ratio"]);

/**
 * Body POST /admin/alerts/rules (@UC9160) — builder CÓ CẤU TRÚC. Client chỉ gửi:
 *  metricKey (từ registry đóng) + comparator + thresholdValue + windowMinutes + severity + channel +
 *  name + runbookUrl. `metric`/`ruleKey`/`condition` do SERVER dẫn xuất từ registry (KHÔNG nhận từ
 *  client → chống injection/over-post). `.strict()` chặn field lạ. no-SMS (channel enum). G4 KHÔNG secret.
 */
export const createAlertRuleSchema = z
  .object({
    metricKey: alertMetricKeySchema,
    name: z.string().min(1).max(ALERT_RULE_NAME_MAX_LENGTH),
    comparator: alertComparatorSchema,
    thresholdValue: thresholdValueString,
    windowMinutes: z.number().int().min(0).nullable().optional(),
    severity: alertSeveritySchema,
    channel: alertChannelSchema,
    runbookUrl: z.string().max(ALERT_RUNBOOK_URL_MAX_LENGTH).nullable().optional(),
    status: alertRuleStatusSchema.optional(),
  })
  .strict();

export type CreateAlertRuleBody = z.infer<typeof createAlertRuleSchema>;
